/**
 * Reading a barcode with the camera already in hand.
 *
 * Typing a model number off a label in a dark mechanical room, with gloves, is how wrong
 * model numbers get into a database. Two paths, both entirely offline:
 *
 *   - `BarcodeDetector`, which Chrome on Android has and which is the fleet's browser.
 *   - Nothing else, said plainly. A bundled decoder is the documented fallback and it is not
 *     bundled here: the honest state is that this works on the phones the technicians carry
 *     and reports its own absence anywhere else, rather than pretending and failing silently
 *     in a basement. `FIELD.md` lists verifying `BarcodeDetector` on the oldest phone in the
 *     fleet as a thing to do before promising the feature, and this is what that verification
 *     is against.
 */

type DetectedBarcode = { rawValue: string; format: string };

type BarcodeDetectorLike = {
  detect: (source: CanvasImageSource | Blob | ImageData) => Promise<DetectedBarcode[]>;
};

const FORMATS = ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'qr_code', 'data_matrix'];

export function scanningSupported(): boolean {
  return typeof (globalThis as { BarcodeDetector?: unknown }).BarcodeDetector === 'function';
}

let detector: BarcodeDetectorLike | null = null;

async function getDetector(): Promise<BarcodeDetectorLike | null> {
  if (!scanningSupported()) return null;
  if (detector) return detector;
  const Ctor = (globalThis as { BarcodeDetector?: new (options: { formats: string[] }) => BarcodeDetectorLike })
    .BarcodeDetector!;
  try {
    detector = new Ctor({ formats: FORMATS });
  } catch {
    detector = new Ctor({ formats: [] });
  }
  return detector;
}

/**
 * Watch a video element until something readable passes in front of it.
 *
 * Returns a stop function rather than a promise that resolves once: a technician scans three
 * parts in a row, and tearing the camera down between them is both slower and uglier than
 * leaving it up.
 */
export async function scan(
  video: HTMLVideoElement,
  onFound: (value: string, format: string) => void,
): Promise<() => void> {
  const detect = await getDetector();
  if (!detect) return () => {};

  let running = true;
  // The same code read twice in consecutive frames is one scan, not two.
  let last = '';
  let lastAt = 0;

  const tick = async () => {
    if (!running) return;
    try {
      const found = await detect.detect(video);
      const first = found[0];
      if (first?.rawValue && (first.rawValue !== last || Date.now() - lastAt > 2500)) {
        last = first.rawValue;
        lastAt = Date.now();
        onFound(first.rawValue, first.format);
      }
    } catch {
      // A frame that could not be decoded is the normal case, not an error.
    }
    if (running) window.setTimeout(() => void tick(), 250);
  };

  void tick();
  return () => {
    running = false;
  };
}
